import { BarChart3, Globe2, Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { Suspense, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import cn from '@/utils/cn';
import { ROUTES } from '@/utils/constants';

interface MisTab {
  to: string;
  label: string;
  icon: typeof BarChart3;
  /** Only the analytics board sits at the shell's own base path. */
  end?: boolean;
}

/**
 * Order is the reading order, not the build order: the two boards that answer a
 * question about the existing book come first, then the two that look outward.
 */
const MIS_TABS: MisTab[] = [
  { to: ROUTES.ADMIN_MIS, label: 'Analytics', icon: BarChart3, end: true },
  { to: ROUTES.ADMIN_MIS_RENEWALS, label: 'Renewals', icon: RefreshCw },
  { to: ROUTES.ADMIN_MIS_FORECAST, label: 'Forecast', icon: TrendingUp },
  { to: ROUTES.ADMIN_MIS_MFD, label: 'MFD Market', icon: Globe2 },
];

interface ThumbBox {
  left: number;
  width: number;
}

/**
 * The shared shell for the four MIS boards: one segmented control, then whichever
 * board the URL names.
 *
 * WHY A LAYOUT ROUTE AND NOT A TABBED PAGE. Each board stays an independent page
 * on its own URL, rendered through `<Outlet />`, so exactly one is mounted at a
 * time. A tab component holding all four would mount all four, and every one of
 * them fires its own MIS queries on mount - four boards' worth of aggregation
 * requests to show one. Routing means the other three are never mounted, never
 * fetched, and (because AppRouter lazy-loads them) never even downloaded until
 * the reader asks for that tab.
 *
 * It also keeps every existing deep link and bookmark working: the URLs are
 * unchanged, only the sidebar collapsed from four rows to one.
 *
 * NOTE ON MODULE ISOLATION: this file deliberately references only ROUTES, never
 * either MIS module. MIS_work_1 and MIS_work_2 may not import each other
 * (CLAUDE.md), and the four page components are wired up in AppRouter - which
 * already imports from both - so no new cross-module edge is created here.
 */
const MisLayout = () => {
  const { pathname } = useLocation();
  const listRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<ThumbBox | null>(null);
  // The first placement must not animate, or the pill visibly slides in from the
  // left edge on every page load. Transitions are enabled only once it has been
  // put in the right place.
  const [animate, setAnimate] = useState(false);

  /**
   * Measures the active tab by asking the DOM which one NavLink marked active.
   *
   * `aria-current="page"` rather than re-deriving the match from `pathname`:
   * `/admin/mis` is a PREFIX of the other three, so the matching rule needs the
   * `end` flag to be applied per tab. React Router already does exactly that,
   * and reading its answer means the indicator can never disagree with which tab
   * is highlighted - which is the bug a second copy of the rule would eventually
   * introduce.
   */
  const measure = useCallback(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!list || !active) {
      setThumb(null);
      return;
    }
    // `offsetLeft` is relative to the nearest positioned ancestor, which is the
    // list itself - and the list deliberately carries no border or padding, so
    // this needs no correction for either.
    setThumb({ left: active.offsetLeft, width: active.offsetWidth });
  }, []);

  // Layout effect, not a plain effect: measuring after paint shows one frame of
  // the pill at its previous size before it corrects.
  useLayoutEffect(() => {
    measure();
  }, [measure, pathname]);

  // Re-measure whenever the control's box changes: collapsing the admin sidebar,
  // resizing the window, or a webfont swapping and changing the label widths.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [measure]);

  // One frame after the pill is first positioned, allow it to animate.
  useLayoutEffect(() => {
    if (!thumb || animate) return undefined;
    const frame = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, [thumb, animate]);

  return (
    <div className="flex flex-col">
      {/* Centred, per the reference: the control is the page's own header, so it
          reads as a switch between views rather than as a filter on one. */}
      <div className="flex justify-center">
        <nav
          aria-label="MIS boards"
          // A pill container with a pill thumb. `rounded-full` on both, and the
          // inset `p-1` is what makes the active pill look seated inside the
          // track instead of painted over it.
          className="inline-flex max-w-full items-center overflow-x-auto rounded-full border border-border bg-secondary/40 p-1"
        >
          {/*
            The positioning context for the sliding pill, and the reason it is a
            separate element from the <nav>: with no border and no padding of its
            own, a tab's `offsetLeft` inside it is exactly the pill's `left`. On
            the <nav> it would be off by the border width, and inside a scrolling
            container that is the kind of one-pixel error that is impossible to
            spot and annoying to chase.
          */}
          <div ref={listRef} className="relative flex items-center gap-1">
            {/*
              ONE pill that moves, rather than a background class that jumps
              between four buttons - swapping a class gives nothing to animate.
              Only `transform` and `width` are transitioned, both compositor-
              friendly; `left` stays 0 so no layout is recalculated per frame.
            */}
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-y-0 left-0 rounded-full bg-primary shadow-sm',
                animate
                  ? 'transition-[transform,width] duration-300 ease-out motion-reduce:transition-none'
                  : 'transition-none',
                thumb ? 'opacity-100' : 'opacity-0',
              )}
              style={
                thumb ? { transform: `translateX(${thumb.left}px)`, width: thumb.width } : undefined
              }
            />

            {MIS_TABS.map(({ to, label, icon: Icon, end }) => (
              // `relative` puts the label above the pill: the pill is positioned
              // and these are not, so without it the pill paints over the text.
              <NavLink key={to} to={to} end={end} className="relative shrink-0">
                {({ isActive }) => (
                  <span
                    className={cn(
                      'inline-flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-xs font-semibold transition-colors sm:min-h-8',
                      isActive
                        ? 'text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    {label}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      {/*
        The boards are lazy-loaded route elements, so switching tabs can suspend
        while the chunk downloads. Without a boundary here React would throw;
        with one, the control stays put and only the board area swaps.
      */}
      <Suspense
        fallback={
          <div className="flex min-h-[60vh] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading board...
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </div>
  );
};

export default MisLayout;
